import { useState, useEffect } from 'react';
import { Trash, PencilSimple, X, FloppyDisk, Star, Image } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';

const EMPTY = { name: '', role: '', company: '', text: '', rating: 5, image_url: '' };

export default function CMSTestimonialsPanel({ token }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchItems = async () => {
    try { const res = await api.get('/cms/testimonials'); setItems(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSave = async () => {
    try {
      if (editing) await api.put(`/admin/cms/testimonials/${editing}?token=${token}`, form);
      else await api.post(`/admin/cms/testimonials?token=${token}`, form);
      setEditing(null); setForm(EMPTY); fetchItems();
    } catch (e) { alert('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try { await api.delete(`/admin/cms/testimonials/${id}?token=${token}`); fetchItems(); }
    catch (e) { alert('Failed to delete'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/admin/upload?token=${token}`, formData);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      setForm(prev => ({ ...prev, image_url: `${API_URL}/api/files/${res.data.path}` }));
    } catch (e) { alert('Upload failed'); }
    finally { setUploading(false); }
  };

  if (loading) return <div className="text-center py-12 text-[#4A5568]">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="cms-testimonials-panel">
      <div className="bg-white border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-[#0A1F3D] mb-4">{editing ? 'Edit Testimonial' : 'Add Testimonial'}</h3>
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Name *</label>
            <Input data-testid="testimonial-name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Customer name" className="rounded-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Role</label>
            <Input value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} placeholder="e.g. Business Traveler" className="rounded-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Company</label>
            <Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} placeholder="Company name" className="rounded-none" />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-medium text-[#4A5568] mb-1">Review Text *</label>
          <Textarea data-testid="testimonial-text-input" value={form.text} onChange={(e) => setForm({...form, text: e.target.value})} rows={3} placeholder="Customer review..." className="rounded-none" />
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Rating</label>
            <div className="flex gap-1">
              {[1,2,3,4,5].map(n => (
                <button key={n} type="button" onClick={() => setForm({...form, rating: n})} className="p-1">
                  <Star size={20} weight={n <= form.rating ? "fill" : "regular"} className={n <= form.rating ? "text-[#D4AF37]" : "text-slate-300"} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1">Photo</label>
            <div className="flex gap-2">
              <Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="Photo URL or upload" className="rounded-none flex-1" />
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                <Button type="button" variant="outline" className="rounded-none" disabled={uploading}>
                  <Image size={16} /> {uploading ? '...' : 'Upload'}
                </Button>
              </label>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="testimonial-save-btn" onClick={handleSave} disabled={!form.name || !form.text} className="bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] rounded-none">
            <FloppyDisk size={16} /> {editing ? 'Update' : 'Add Testimonial'}
          </Button>
          {editing && <Button variant="outline" onClick={() => { setEditing(null); setForm(EMPTY); }} className="rounded-none"><X size={16} /> Cancel</Button>}
        </div>
      </div>

      <div className="bg-white border border-slate-200">
        <div className="p-4 border-b border-slate-200"><h3 className="font-bold text-[#0A1F3D]">{items.length} Testimonials</h3></div>
        {items.length === 0 ? (
          <div className="text-center py-8 text-[#4A5568]">No testimonials yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((item) => (
              <div key={item.id} className="flex items-start gap-4 p-4 hover:bg-[#F5F5F5]/50">
                {item.image_url && <img src={item.image_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-[#0A1F3D] text-sm">{item.name}</h4>
                    <div className="flex">{Array.from({length: item.rating}, (_, i) => <Star key={i} size={12} weight="fill" className="text-[#D4AF37]" />)}</div>
                  </div>
                  {item.role && <p className="text-xs text-[#D4AF37]">{item.role}{item.company ? ` at ${item.company}` : ''}</p>}
                  <p className="text-xs text-[#4A5568] mt-1 line-clamp-2">{item.text}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => { setEditing(item.id); setForm({ name: item.name, role: item.role, company: item.company, text: item.text, rating: item.rating, image_url: item.image_url }); }} className="p-2 hover:bg-slate-100 text-[#4A5568] hover:text-[#0A1F3D]"><PencilSimple size={16} /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-red-50 text-[#4A5568] hover:text-red-500"><Trash size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
